import { useEffect, useState } from "react";
import axios from "axios";
import { GoogleGenerativeAI } from "@google/generative-ai";
import Navbar from "../navbar/Navbar";

const genAI = new GoogleGenerativeAI(process.env.REACT_APP_API_KEY);

function MoodAnalyzer() {
  const [data, setData] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);

  const username = JSON.parse(localStorage.getItem("user")).username;

  useEffect(() => {
    const analyze = async () => {
      try {
        const res = await axios.post(`https://happyu.onrender.com/analyse/${username}`);
        setData(res.data);

        const prompt = `
You are an expert behavioral psychologist.

Your task:
Analyze the following user data (mood tracker and journal entries) and produce insights.

Return the output STRICTLY in this JSON format — no HTML, no Markdown, no explanations.

{
  "moodSummary": "Short overview of the user's general mood patterns over time.",
  "emotionalInsights": "Deeper emotional analysis describing trends, emotions, and their meanings.",
  "possibleTriggers": ["List of 3–5 possible emotional or situational triggers."],
  "suggestions": ["List of 3–5 practical well-being or coping suggestions."]
}

Context:
User Mood Tracker:
${JSON.stringify(res.data.moodData, null, 2)}

User Journals:
${JSON.stringify(res.data.journals, null, 2)}

⚠️ Rules:
- Do NOT output anything except valid JSON with these four keys.
- Do NOT include comments, HTML, Markdown, or explanations.
- Keep the tone empathetic, concise, and professional.
`;

        const model = genAI.getGenerativeModel({ model: "gemini-2.0-flash" });
        const result = await model.generateContent(prompt);
        let text = result.response.text().trim();

        // 🔧 Sanitize output — remove ```json or ``` wrappers
        if (text.startsWith("```")) {
          text = text.replace(/```json|```/g, "").trim();
        }

        const parsed = JSON.parse(text);
        setAnalysis(parsed);
      } catch (err) {
        console.error("Error during analysis:", err);
        setAnalysis({
          moodSummary: "Could not fetch analysis.",
          emotionalInsights: "",
          possibleTriggers: [],
          suggestions: [],
        });
      } finally {
        setLoading(false);
      }
    };

    analyze();
  }, [username]);

  if (loading)
    return (
        
      <div className="flex justify-center items-center h-screen bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="text-center">
          <div className="loader mx-auto mb-4"></div>
          <p className="text-lg text-gray-700 font-medium">
            Analyzing your emotional patterns...
          </p>
        </div>
        <style>{`
          .loader {
            border: 6px solid #eee;
            border-top: 6px solid #6b5bff;
            border-radius: 50%;
            width: 50px;
            height: 50px;
            animation: spin 1s linear infinite;
          }
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );

  if (!analysis) return <p>No analysis data available.</p>;

  return (
    <>
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 p-8 flex justify-center items-center">
      <div className="w-full max-w-4xl bg-white rounded-3xl shadow-xl p-8 space-y-6 border border-gray-100">
        <h2 className="text-3xl font-bold text-center text-gray-800 mb-6">
          🧠 Mood & Behavior Analysis Dashboard
        </h2>

        <div className="grid md:grid-cols-2 gap-6">
          <Card
            title="Mood Summary"
            text={analysis.moodSummary}
            color="from-blue-100 to-blue-50"
          />
          <Card
            title="Emotional Insights"
            text={analysis.emotionalInsights}
            color="from-pink-100 to-pink-50"
          />
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <ListCard
            title="Possible Triggers"
            items={analysis.possibleTriggers}
            color="from-yellow-100 to-yellow-50"
          />
          <ListCard
            title="Well-being Suggestions"
            items={analysis.suggestions}
            color="from-green-100 to-green-50"
          />
        </div>
      </div>
    </div>
    </>
  );
}

function Card({ title, text, color }) {
  return (
    <div className={`bg-gradient-to-br ${color} rounded-2xl p-6 shadow-md`}>
      <h3 className="text-xl font-semibold text-gray-800 mb-2">{title}</h3>
      <p className="text-gray-700 leading-relaxed">{text}</p>
    </div>
  );
}

function ListCard({ title, items, color }) {
  return (
    <div className={`bg-gradient-to-br ${color} rounded-2xl p-6 shadow-md`}>
      <h3 className="text-xl font-semibold text-gray-800 mb-3">{title}</h3>
      <ul className="list-disc pl-5 space-y-2 text-gray-700">
        {items.map((item, idx) => (
          <li key={idx} className="leading-relaxed">
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default MoodAnalyzer;
